// useMe hook.

import { useEffect, useState } from 'react';
import { fetchMe, ApiError, ApiUnreachableError, type MeResponse } from '@/lib/api';
import { setDevNowOverride } from '@/lib/devNow';
import { identify } from '@/lib/analytics/client';

export type Role = MeResponse['role'];

interface MeState {
  state: 'loading' | 'unauth' | 'ok' | 'offline';
  me?: MeResponse;
}

let cached: MeResponse | null = null;
let inflight: Promise<MeResponse | null> | null = null;

function loadMe(): Promise<MeResponse | null> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetchMe()
    .then((m) => {
      cached = m;
      // Dev-only: pin the shared clock so header dates/times match the visual-parity mock.
      setDevNowOverride(m.workspace?.dev_now ?? null);
      return m;
    })
    .catch((e) => {
      if (e instanceof ApiUnreachableError) return null;
      // 401 returns ApiError; rethrow so caller can branch.
      throw e;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function isAdmin(
  input: Role | undefined | null | { role: Role | undefined; is_zoe_admin?: boolean }
): boolean {
  const role = typeof input === 'object' && input !== null ? input.role : input;
  const isZoeAdmin = typeof input === 'object' && input !== null ? input.is_zoe_admin === true : false;
  return role === 'owner' || role === 'admin' || isZoeAdmin;
}

/** Drop the shared /api/me cache so the next `useMe` consumer re-fetches. */
export function invalidateMe(): void {
  cached = null;
  inflight = null;
}

/** `enabled: false` skips the `/api/me` fetch entirely and reports `unauth`. */
export function useMe(options?: { enabled?: boolean }): MeState {
  const enabled = options?.enabled !== false;
  const [s, setS] = useState<MeState>(() =>
    !enabled ? { state: 'unauth' } : cached ? { state: 'ok', me: cached } : { state: 'loading' },
  );

  useEffect(() => {
    if (!enabled) {
      setS({ state: 'unauth' });
      return;
    }
    let cancelled = false;
    loadMe()
      .then((m) => {
        if (cancelled) return;
        if (m === null) setS({ state: 'offline' });
        else {
          setS({ state: 'ok', me: m });
          // Fire the anon->user merge once a session has actually resolved to a user.
          identify(m.user_id);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setS({ state: error instanceof ApiError && error.status === 401 ? 'unauth' : 'offline' });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return s;
}

// Test helper.
export function _resetMeCache() {
  cached = null;
  inflight = null;
}
